/**
 * Nënshkrimi me dorë — kanavacë, jo fushë teksti.
 *
 * Një emër i shtypur nuk është nënshkrim: procesverbali i dorëzimit lexohet
 * pikërisht atëherë kur dikush e mohon se e mori pajisjen, dhe atëherë vlen
 * vetëm gjurma e dorës.
 *
 * Ruhet si `data:image/png;base64,…` te `asset_handovers`. Formati zgjidhet
 * ashtu që dokumenti të mbetet i lexueshëm pa varësi nga një skedar i jashtëm
 * që mund të fshihet nga disku.
 *
 * Kanavaca punon me `pointer events`, ndaj funksionon njësoj me mausin, me
 * gishtin dhe me stilolapsin — HR-ja që e nënshkruan te tableti është rasti i
 * zakonshëm, jo përjashtimi.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

const W = 420;
const H = 120;

export default function SignaturePad({ label, value, onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [empty, setEmpty] = useState(!value);

  /** Vizato vlerën ekzistuese kur dialogu rihapet me një nënshkrim të ruajtur. */
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    setEmpty(!value);
    if (!value) return;
    const img = new Image();
    img.onload = () => ctx.drawImage(img, 0, 0, W, H);
    img.src = value;
  }, [value]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    // Kanavaca vizatohet në koordinatat e veta (W×H) por shfaqet e shkallëzuar;
    // pa këtë raport, vija ndjek gishtin me zhvendosje.
    return {
      x: (e.clientX - r.left) * (W / r.width),
      y: (e.clientY - r.top)  * (H / r.height),
    };
  };

  const start = (e) => {
    if (disabled) return;
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setEmpty(false);
  };

  const end = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    if (!canvasRef.current) return;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  }, [onChange]);

  function clear() {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    setEmpty(true);
    onChange?.('');
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                          textTransform: 'uppercase', color: 'text.secondary' }}>
          {label}
        </Typography>
        {!disabled && !empty && (
          <Button size="small" startIcon={<ClearIcon sx={{ fontSize: 14 }} />} onClick={clear}
            sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, py: 0 }}>
            Pastro
          </Button>
        )}
      </Stack>
      <Box
        component="canvas"
        ref={canvasRef}
        width={W}
        height={H}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        sx={{
          width: '100%', height: 96, display: 'block', touchAction: 'none',
          border: '1px dashed', borderColor: empty ? 'divider' : 'primary.main',
          borderRadius: 1.5, bgcolor: disabled ? 'grey.50' : 'background.paper',
          cursor: disabled ? 'not-allowed' : 'crosshair',
        }}
      />
      {empty && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          Nënshkruaj me maus, gisht ose stilolaps.
        </Typography>
      )}
    </Box>
  );
}
